import React, { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from '../hooks/use-i18n'
import { formatPoints } from '../utils/points-label'
import { getPublicPlayerPredictions } from '../services/publicService'
import type { LeaderboardEntry, PublicPlayerPredictionItem } from '../types/public'
import { getMatchDate, formatDate } from '../utils/dateHelper'
import MatchTeamsInline from './MatchTeamsInline'
import { footballDisplayScore, footballLiveBadge, FOOTBALL_LIVE_POLL_MS, isFootballMatchLive } from '../utils/footballLiveScore'

function computePoints(item: PublicPlayerPredictionItem): number {
  const { prediction, match } = item
  if (!match?.score || match.winner === undefined) return 0
  const predictedWinner = prediction.winner ? String(prediction.winner).trim() : null
  const actualWinner = match.winner ? String(match.winner).trim() : null
  const winnerCorrect = predictedWinner === actualWinner
  let winnerPoint = 0
  if (winnerCorrect) winnerPoint = match.winner ? 1 : 2
  const predScoreStr = String(prediction.predictedScore || '').trim()
  const matchScoreStr = String(match.score || '').trim()
  let scorePoint = 0
  if (predScoreStr && matchScoreStr && predScoreStr === matchScoreStr && winnerCorrect) {
    const rules: Record<string, number> = {
      '1-0': 1, '2-0': 1, '2-1': 1, '0-0': 2, '1-1': 2, '2-2': 2,
      '3-0': 2, '3-1': 2, '3-2': 2, '3-3': 3, '4-0': 3, '4-1': 3, '4-2': 3, '4-3': 3, '4-4': 4,
    }
    scorePoint = rules[predScoreStr] ?? 1
  }
  return winnerPoint + scorePoint
}

/** Winner / score split (room-style breakdown). Prefers backend point fields, else mirrors computePoints. */
function computePointsSplit(item: PublicPlayerPredictionItem): { winnerPoint: number; scorePoint: number } {
  const { prediction, match } = item
  if (prediction.predictedWinnerPoint != null || prediction.predictedScorePoint != null) {
    return {
      winnerPoint: Number(prediction.predictedWinnerPoint) || 0,
      scorePoint: Number(prediction.predictedScorePoint) || 0,
    }
  }
  if (!match?.score || match.winner === undefined) return { winnerPoint: 0, scorePoint: 0 }
  const predictedWinner = prediction.winner ? String(prediction.winner).trim() : null
  const actualWinner = match.winner ? String(match.winner).trim() : null
  const winnerCorrect = predictedWinner === actualWinner
  let winnerPoint = 0
  if (winnerCorrect) winnerPoint = match.winner ? 1 : 2
  const predScoreStr = String(prediction.predictedScore || '').trim()
  const matchScoreStr = String(match.score || '').trim()
  let scorePoint = 0
  if (predScoreStr && matchScoreStr && predScoreStr === matchScoreStr && winnerCorrect) {
    const rules: Record<string, number> = {
      '1-0': 1, '2-0': 1, '2-1': 1, '0-0': 2, '1-1': 2, '2-2': 2,
      '3-0': 2, '3-1': 2, '3-2': 2, '3-3': 3, '4-0': 3, '4-1': 3, '4-2': 3, '4-3': 3, '4-4': 4,
    }
    scorePoint = rules[predScoreStr] ?? 1
  }
  return { winnerPoint, scorePoint }
}

function buildWinnerSuffix(winner: string | null | undefined, t: (k: string, o?: any) => string): string {
  const w = winner ? String(winner).trim() : ''
  if (!w || w === 'null') return ` (${t('stats.draw')})`
  return ` (${t('stats.teamWins', { team: w })})`
}

export interface PublicPlayerStatsGamesModalProps {
  entry: LeaderboardEntry | null
  tournamentId: string | null
  /** When "All leagues" is selected, pass all tournament IDs so the modal shows all predicted games. */
  tournamentIds?: string[]
  tournamentName: string
  viewType: 'stats' | 'games'
  isOpen: boolean
  onClose: () => void
  /** When provided, "My prediction" is shown only when entry.userId === currentUserId; otherwise player-agnostic label. */
  currentUserId?: string
}

const PublicPlayerStatsGamesModal: React.FC<PublicPlayerStatsGamesModalProps> = ({
  entry,
  tournamentId,
  tournamentIds,
  tournamentName,
  viewType,
  isOpen,
  onClose,
  currentUserId,
}) => {
  const { t } = useTranslation()
  const isViewingOwn = Boolean(currentUserId && entry?.userId && String(entry.userId).trim() === String(currentUserId).trim())
  const predictionLabel = isViewingOwn ? t('publicFootball.myPrediction') : t('stats.playerScoreLabel')
  const idsToFetch = (tournamentIds?.length ? tournamentIds : tournamentId ? [tournamentId] : []) as string[]
  const idsKey = [...idsToFetch].sort().join(',')

  // Reuse same query key as parent when viewing own predictions so modal opens without refetch
  const queryKey = isViewingOwn && currentUserId
    ? ['public-my-predictions', currentUserId, idsKey]
    : ['public-player-predictions', entry?.userId ?? '', idsKey]

  const { data: items = [], isLoading: loading } = useQuery({
    queryKey,
    queryFn: () => getPublicPlayerPredictions(entry!.userId, idsToFetch),
    enabled: isOpen && !!entry && idsToFetch.length > 0,
    staleTime: 30 * 1000,
    refetchInterval: isOpen ? FOOTBALL_LIVE_POLL_MS : false,
  })

  const sorted = useMemo(
    () => [...items].sort((a, b) => new Date(getMatchDate(b.match, true)).getTime() - new Date(getMatchDate(a.match, true)).getTime()),
    [items]
  )

  const withResult = sorted.filter((i) => i.match != null && i.match.score != null && String(i.match.score).trim() !== '')
  const totalPoints = useMemo(() => withResult.reduce((sum, i) => sum + computePoints(i), 0), [withResult])

  if (!isOpen) return null

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
      onClick={handleBackdrop}
    >
      <div
        className="w-full max-w-2xl glass-strong rounded-xl sm:rounded-2xl p-4 sm:p-6 max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start gap-2 mb-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary flex items-center justify-center shrink-0">
              <span className="text-primary-foreground font-bold text-lg">
                {(entry?.userName ?? '?').charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-display font-bold text-foreground truncate">{entry?.userName ?? ''}</h2>
              <p className="text-sm text-muted-foreground truncate">{tournamentName}</p>
              {viewType === 'stats' && (
                <p className="text-sm font-semibold text-primary mt-0.5">{formatPoints(totalPoints, t)} · {withResult.length} {t('roomPage.games')}</p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground shrink-0" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
            </div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>{t('publicFootball.noResultsYet')}</p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {sorted.map((item) => {
                const hasResult = item.match != null && item.match.score != null && String(item.match.score).trim() !== ''
                const { winnerPoint, scorePoint } = computePointsSplit(item)
                const totalPoints = winnerPoint + scorePoint
                const matchScoreStr = footballDisplayScore(item.match)
                const liveBadge = footballLiveBadge(item.match)
                return (
                  <div key={item.prediction.id} className="glass-card rounded-lg sm:rounded-xl p-3 sm:p-4">
                    <div className="space-y-1.5 sm:space-y-2">
                      <div className="flex items-start justify-between gap-2 sm:gap-3">
                        <div className="flex items-center flex-wrap gap-2 min-w-0 flex-1">
                          <MatchTeamsInline
                            teamA={item.match.teamA}
                            teamB={item.match.teamB}
                            league={item.match.league}
                            separator="-"
                            nameClassName="font-semibold text-small sm:text-body text-foreground"
                          />
                          {hasResult && matchScoreStr !== '' && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-lg font-semibold text-xs sm:text-small border shrink-0 ${isFootballMatchLive(item.match) ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-primary/20 text-primary border-primary/30'}`}>
                              {liveBadge ? `${liveBadge} · ` : ''}{t('roomPage.realScore')}: {matchScoreStr}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground shrink-0 text-right whitespace-nowrap pt-0.5">
                          {formatDate(getMatchDate(item.match), true)}
                        </span>
                      </div>
                      <div className="text-primary font-medium text-small sm:text-body">
                        {predictionLabel} {item.prediction.predictedScore}{buildWinnerSuffix(item.prediction.winner, t)}
                      </div>
                      {hasResult && (
                        <div className="mt-2 sm:mt-3 flex flex-wrap items-center gap-3 sm:gap-4 text-small sm:text-body">
                          <span className="text-muted-foreground">
                            {t('stats.resultLabel')}:{' '}
                            <span className="font-semibold text-foreground">{winnerPoint}</span>
                          </span>
                          <span className="text-muted-foreground">
                            {t('stats.scoreLabel')}:{' '}
                            <span className="font-semibold text-foreground">{scorePoint}</span>
                          </span>
                          <span
                            className={`ml-auto inline-flex items-center px-3 py-1 rounded-lg font-bold ${
                              totalPoints === 0 ? 'bg-destructive/20 text-destructive' : 'bg-success/20 text-success'
                            }`}
                          >
                            {formatPoints(totalPoints, t)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PublicPlayerStatsGamesModal
